import { useWatch } from "react-hook-form";
import { useDesignProjectPrices, type UseDesignProjectPricesParams } from "@design-lab/shared/api";
import { useCompositionsList } from "@design-lab/entities/composition";
import { useDebounce } from "common/hooks/useDebounce";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { useVariants } from "./useVariants";

export function useDesignLabPrices(placements: string[], assemblyGroupId?: string | null) {
  const { formCompositions } = useCompositionsList();
  const { getVariantQuantity } = useVariants();

  const { form: artworkForm } = useArtworkForm();
  const artworkFormData = useWatch({
    control: artworkForm.control
  });

  const { info, back, front } = artworkFormData || {};

  const enhancementTiers: Record<string, { tier: string; hasExpertEditAddon: boolean }> = {};

  if (front?.artworkId) {
    const frontTier = info?.front?.enhancementTier || "basic";
    enhancementTiers["front"] = {
      tier: frontTier,
      hasExpertEditAddon:
        (info?.front?.jiffyDesignServices || false) && frontTier === "full_enhancements"
    };
  }
  if (back?.artworkId) {
    const backTier = info?.back?.enhancementTier || "basic";
    enhancementTiers["back"] = {
      tier: backTier,
      hasExpertEditAddon:
        (info?.back?.jiffyDesignServices || false) && backTier === "full_enhancements"
    };
  }

  const variantQuantities: Record<string, number> = {};

  formCompositions?.forEach(formComposition => {
    Object.keys(formComposition.variants).forEach(variantId => {
      const qty = getVariantQuantity(formComposition.id, variantId) || 0;
      if (qty <= 0) return;

      variantQuantities[variantId] = (variantQuantities[variantId] || 0) + qty;
    });
  });

  const hasQty = Object.values(variantQuantities).some(qty => qty > 0);
  const hasPlacements = placements && placements.length > 0;

  const params =
    hasQty && hasPlacements
      ? { variantQuantities, placements, assemblyGroupId, enhancementTiers }
      : undefined;

  const stringifiedParams = params ? JSON.stringify(params) : undefined;
  const debouncedString = useDebounce(stringifiedParams, 1000) as string | undefined;
  const debouncedParams = debouncedString
    ? (JSON.parse(debouncedString) as UseDesignProjectPricesParams)
    : undefined;

  return useDesignProjectPrices(debouncedParams);
}
